import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { formatISO } from 'date-fns';
import { Task } from '@/types/tasks';
import { ViewMode } from '../TaskViewContainer';
import TaskBoard from '../TaskBoard';
import TaskBoardLoading from './TaskBoardLoading';
import EmptyTaskState from './EmptyTaskState';

interface TaskBoardContainerProps {
  tasks: Task[];
  viewMode: ViewMode;
  currentDate: Date;
  isTransitioning: boolean;
  isLoading?: boolean; // NEW: Accept loading state as prop instead of using context
  onAddTask?: (taskData: Omit<Task, 'id' | 'isCompleted' | 'createdAt'>) => void;
  onCreateTask?: () => void;
  customTaskOperations?: {
    completeTask?: (taskId: string) => void;
    uncompleteTask?: (taskId: string) => void;
    removeTask?: (taskId: string) => void;
    updateTask?: (taskId: string, updates: Partial<Task>, silent?: boolean) => void;
  };
  // NEW: TaskManager-specific operations for immediate database persistence
  taskManagerOperations?: {
    updateTask: (taskId: string, updates: Partial<Task>, silent?: boolean) => void;
    updateTaskOrder?: (taskId: string, newOrder: number) => void;
  };
  // NEW: Project filter props
  selectedProjectId?: string | null;
  onProjectFilterChange?: (projectId: string | null) => void;
  allTasks?: any[];
  projectTaskCounts?: {
    projectCounts: Record<string, number>;
    totalTasks: number;
  };
  // NEW: Task editing props
  editingTaskId?: string | null;
  onCloseEdit?: () => void;
}

const TaskBoardContainer: React.FC<TaskBoardContainerProps> = React.memo(({
  tasks,
  viewMode,
  currentDate,
  isTransitioning,
  isLoading = false, // NEW: Default to false
  onAddTask,
  onCreateTask,
  customTaskOperations,
  taskManagerOperations,
  selectedProjectId,
  onProjectFilterChange,
  allTasks,
  projectTaskCounts,
  editingTaskId,
  onCloseEdit
}) => {
  // ✅ HOOKS RULE: All hooks must be called before any conditional returns
  // Create a unique key for the current view to ensure proper animation
  const viewModeKey = useMemo(() => 
    `${viewMode}-${formatISO(currentDate, { representation: 'date' })}`,
    [viewMode, currentDate]
  );
  
  // Only check for empty state AFTER loading is complete
  const hasNoTasks = useMemo(() => !tasks || tasks.length === 0, [tasks]);
  
  // Show skeleton during loading OR transitions
  if (isLoading || isTransitioning) {
    return <TaskBoardLoading activeTaskCount={3} completedTaskCount={2} showCompletedExpanded={false} />;
  }

  return (
    <div className="w-full min-h-[400px] relative">
      <div className="w-full">
        {!hasNoTasks ? (
          <TaskBoard 
            initialTasks={tasks}
            onAddTask={onAddTask}
            viewMode={viewMode}
            currentDate={currentDate}
            isTransitioning={isTransitioning}
            onCreateTask={onCreateTask}
            customTaskOperations={customTaskOperations}
            taskManagerOperations={taskManagerOperations}
            selectedProjectId={selectedProjectId}
            onProjectFilterChange={onProjectFilterChange}
            allTasks={allTasks}
            projectTaskCounts={projectTaskCounts}
            editingTaskId={editingTaskId}
            onCloseEdit={onCloseEdit}
          />
        ) : (
          <EmptyTaskState viewMode={viewMode} currentDate={currentDate} onCreateTask={onCreateTask} />
        )}
      </div>
    </div>
  );
});

TaskBoardContainer.displayName = 'TaskBoardContainer';

export default TaskBoardContainer;
